'use strict';

/**
 * Org Activity -- an org admin's view of their own tenant's user activity (GET /1.0/Org/Activity
 * via providers.OpsAPI, tenant-scoped + server-gated to org admins). A simple reverse-chronological
 * feed. Template-driven per the Pict conventions; no innerHTML. See design/Observability-Plan.md.
 */

const libPictView = require('pict-view');

class PictViewOrgActivity extends libPictView
{
	_data() { return this.pict.AppData.OrgActivity || {}; }
	_api() { return this.pict.providers.OpsAPI; }

	_ms(pValue)
	{
		if (!pValue) { return 0; }
		let tmpString = String(pValue);
		let tmpParsed = Date.parse(tmpString);
		if (!isNaN(tmpParsed)) { return tmpParsed; }
		tmpParsed = Date.parse(tmpString.replace(' ', 'T') + 'Z');
		return isNaN(tmpParsed) ? 0 : tmpParsed;
	}

	_ago(pValue)
	{
		let tmpMs = this._ms(pValue);
		if (!tmpMs) { return ''; }
		let tmpSeconds = Math.max(0, Math.floor((Date.now() - tmpMs) / 1000));
		if (tmpSeconds < 60) { return tmpSeconds + 's ago'; }
		let tmpMinutes = Math.floor(tmpSeconds / 60);
		if (tmpMinutes < 60) { return tmpMinutes + 'm ago'; }
		let tmpHours = Math.floor(tmpMinutes / 60);
		if (tmpHours < 24) { return tmpHours + 'h ago'; }
		return Math.floor(tmpHours / 24) + 'd ago';
	}

	onBeforeRender(pRenderable)
	{
		let tmpD = this._data();
		tmpD.Rows = (tmpD.Rows || []).map((pRow) => (
		{
			User: pRow.User || ('User ' + pRow.IDUser),
			Summary: (pRow.Action || 'did') + ' ' + (pRow.TargetType || '') + (pRow.IDTarget ? (' #' + pRow.IDTarget) : ''),
			Ago: this._ago(pRow.CreateDate),
			When: pRow.CreateDate || ''
		}));
		tmpD.RowsEmpty = (tmpD.Rows.length) ? [] : [1];
		tmpD.LoadErrorSlot = tmpD.LoadError ? [{ Message: tmpD.LoadError }] : [];
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	show() { return this.refresh(); }

	refresh()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.loadOrgActivity !== 'function') { this.render(); return Promise.resolve(); }
		return tmpAPI.loadOrgActivity({ count: 150 }).then(() => this.render());
	}
}

module.exports = PictViewOrgActivity;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-OrgActivity',
	DefaultRenderable: 'Plansheet-OrgActivity',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-act { padding: 20px 24px 60px; max-width: 880px; }
		.ps-act-head { display: flex; align-items: center; gap: 14px; margin-bottom: 6px; }
		.ps-act-head h1 { font-size: 22px; font-weight: 600; margin: 0; flex: 1; color: var(--theme-color-text-primary, #2c3140); }
		.ps-act-refresh { padding: 7px 14px; font-size: 13px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-act-refresh:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-act-sub { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 16px; }
		.ps-act-err { background: var(--theme-color-status-error, #cc3333); color: #fff; padding: 8px 12px; border-radius: 8px; margin-bottom: 14px; font-size: 13px; }
		.ps-act-empty { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 13px; padding: 10px 2px; }
		.ps-act-row { display: flex; gap: 10px; align-items: baseline; padding: 8px 10px; border-radius: 6px; font-size: 13px;
			border-bottom: 1px solid var(--theme-color-border-light, #eef1f6); }
		.ps-act-row:hover { background: var(--theme-color-background-hover, #eef1f6); }
		.ps-act-user { font-weight: 600; color: var(--theme-color-text-primary, #2c3140); min-width: 120px; }
		.ps-act-what { flex: 1; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-act-when { flex: 0 0 auto; font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-OrgActivity',
			Template: /*html*/`
<div class="ps-act">
	<div class="ps-act-head">
		<h1>Activity</h1>
		<button class="ps-act-refresh" onclick="{~P~}.views['Plansheet-OrgActivity'].refresh()">Refresh</button>
	</div>
	<p class="ps-act-sub">What your team has been doing, most recent first.</p>
	{~TS:OrgAct-LoadError:AppData.OrgActivity.LoadErrorSlot~}
	{~TS:OrgAct-Empty:AppData.OrgActivity.RowsEmpty~}
	{~TS:OrgAct-Row:AppData.OrgActivity.Rows~}
</div>`
		},
		{ Hash: 'OrgAct-LoadError', Template: /*html*/`<div class="ps-act-err">Could not load activity: {~D:Record.Message~}</div>` },
		{ Hash: 'OrgAct-Empty', Template: /*html*/`<p class="ps-act-empty">No activity recorded yet.</p>` },
		{ Hash: 'OrgAct-Row', Template: /*html*/`<div class="ps-act-row"><span class="ps-act-user">{~D:Record.User~}</span><span class="ps-act-what">{~D:Record.Summary~}</span><span class="ps-act-when" title="{~D:Record.When~}">{~D:Record.Ago~}</span></div>` }
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-OrgActivity',
			TemplateHash: 'Plansheet-OrgActivity',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
